import $http from '@/api';
import store from '@/store';
import { sortObjectPropertiesByKey as sort } from '@/utils';
import { i18n } from '@/plugins/vue-i18n';

const cache = new Map();

export class CompositionSaveError extends Error {
  constructor(message) {
    super(message);
    this.name = 'CompositionSaveError';
  }
}

export default {
  /**
   * Fetches the composition.
   * @return {Promise<{}>}
   */
  async getComposition(compositionId) {
    return $http.get(`/api/board/composition/${compositionId}`);
  },
  /**
   * Fetches the composition configuration.
   * @return {Promise<{}>}
   */
  async getCompositionConfig(compositionId) {
    return $http.get(`/api/board/composition/${compositionId}/config`);
  },
  /**
   * Fetches the composition editor service.
   * @return {Promise<{}>}
   */
  async getCompositionEditor(compositionId, compositionRelativeVersionId = null) {
    return compositionRelativeVersionId
      ? $http.get(`/api/board/composition/${compositionId}/versions/editor/${compositionRelativeVersionId}`)
      : $http.get(`/api/board/composition/${compositionId}/versions/editor`);
  },
  /**
   * Fetches the composition card.
   * @return {Promise<{}>}
   */
  async getCompositionVersionCard(compositionId, compositionRelativeVersionId = null) {
    return compositionRelativeVersionId
      ? $http.get(`/api/board/composition/${compositionId}/versions/card/${compositionRelativeVersionId}`)
      : $http.get(`/api/board/composition/${compositionId}/versions/card`);
  },

  /**
   * Updates the given composition, saving the actual draft.
   * @param {Number} compositionId - The composition's identifier
   * @param {Number} compositionVersionId - The composition's version identifier
   * @param {{}} payload - The composition's payload.
   * @return {*}
   */
  updateComposition(compositionId, compositionVersionId, payload) {
    return $http.patch(`/api/board/composition/${compositionId}/versions/editor/${compositionVersionId}`, payload)
      .catch((error) => {
        const { response } = error;
        const [err] = response.data.errors;
        const message = i18n.te(`message.composition.error.saving.${err.type}`)
          ? i18n.t(`message.composition.error.saving.${err.type}`, { compositionVersionId })
          : i18n.t('message.composition.error.saving.default');
        throw new CompositionSaveError(message);
      });
  },

  /**
   * Publish the given composition.
   * @param {Number} compositionId - The composition's identifier
   * @param {Number} compositionVersionId - The composition's version identifier
   * @return {*}
   */
  publishComposition(compositionId, compositionVersionId) {
    return $http.post(`/api/board/composition/${compositionId}/versions/${compositionVersionId}/publish`);
  },

  /**
   * Publish the given composition.
   * @param {Number} compositionId - The composition's identifier
   * @param {Number} compositionVersionId - The composition's version identifier
   * @param {String|null} schedulePublishDate
   * @return {*}
   */
  scheduleComposition(compositionId, compositionVersionId, schedulePublishDate) {
    return $http.post(`/api/board/composition/${compositionId}/versions/${compositionVersionId}/schedule`, {
      schedulePublish: schedulePublishDate,
    });
  },

  /**
   * Clone the given composition.
   * @param {Number} compositionId - The composition's identifier
   * @param {Number} compositionVersionId - The composition's version identifier
   * @return {*}
   */
  cloneComposition(compositionId, compositionVersionId) {
    return $http.post(`/api/board/composition/${compositionId}/versions/${compositionVersionId}/clone`);
  },

  /**
   * Create new empty version
   * @param {Number} compositionId - The composition's identifier
   * @param {Number} type - The desired composition type
   * @return {*}
   */
  createEmptyVersion(compositionId, type) {
    return $http.post(`/api/board/composition/${compositionId}/new-version`, {
      type,
    });
  },

  /**
   * Create a new composition
   * @param {Object} payload
   * @return {*}
   */
  async createComposition(payload) {
    const context = store.getters.context;
    return $http.post('/api/board/composition', { ...payload, context });
  },

  async createCompositionFromModule({
    moduleId, compositionId, compositionVersionId, alias,
  }) {
    return $http.post(
      `/api/board/composition/${compositionId}/versions/${compositionVersionId}/create-composition-from-module/${moduleId}`,
      { alias },
    );
  },

  async createModuleFromCompositionModule({ moduleId, compositionId, compositionVersionId }) {
    return $http.post(
      `/api/board/composition/${compositionId}/versions/${compositionVersionId}/create-module-from-composition-module-embed/${moduleId}`,
    );
  },

  /**
   * Delete the given composition.
   * @param compositionId
   * @return {*}
   */
  deleteComposition(compositionId) {
    return $http.delete(`/api/board/composition/${compositionId}`);
  },

  /**
   * Unpublish the given composition.
   * @param compositionId
   * @return {*}
   */
  unpublishComposition(compositionId) {
    return $http.post(`/api/board/composition/${compositionId}/unpublish`);
  },

  /**
   * Get unpublished contents from the given composition version.
   * @param compositionId
   * @param compositionVersionId
   * @return {*}
   */
  unpublishedContents(compositionId, compositionVersionId) {
    return $http.get(`/api/board/composition/${compositionId}/versions/${compositionVersionId}/unpublished_contents`);
  },

  getCompositionVersions(compositionId, page = 1, limit = 20) {
    return $http.post(`/api/board/composition/${compositionId}/versions`, { page, limit });
  },

  /**
   * @return {*}
   */
  async moduleGetFields(payload) {
    if (!payload.moduleType
      || !payload.fields
      || !Array.isArray(payload.fields)
      || payload.fields.length === 0) {
      console.error('Missing params trying to fetch module fields: missing required field', payload);
      return {};
    }

    const cacheKey = JSON.stringify(sort({
      fn: 'moduleGetFields',
      moduleType: payload.moduleType,
      slotType: payload.slotType,
      parentModuleType: payload.parentModuleType,
      parentModuleTemplate: payload.parentModuleTemplate,
      template: payload.template,
      fields: payload.fields,
    }));
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey);
    }

    try {
      const response = await $http.post('/api/board/module/getFields', payload);
      cache.set(cacheKey, response);
      return response;
    } catch (e) {
      return {};
    }
  },

  async moduleRecalculateFields({
    fields = [], moduleType, template, slotType, parentModuleType, parentModuleTemplate,
  }) {
    if (!moduleType
      || !slotType
      || fields.length === 0) {
      console.error('Missing params trying to recalculate module fields');
    }
    try {
      return $http.post('/api/board/module/recalculateFields', {
        fields, moduleType, template, slotType, parentModuleType, parentModuleTemplate,
      });
    } catch (e) {
      return {};
    }
  },

  async moduleGetFieldList(payload) {
    const cacheKey = JSON.stringify(sort({
      fn: 'moduleGetFieldList',
      ...payload,
    }));
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey);
    }

    try {
      const response = await $http.post('/api/board/module/getFieldList/', payload);
      const fields = response.fields || [];
      cache.set(cacheKey, fields);
      return fields;
    } catch (e) {
      return [];
    }
  },

  async moduleGetConfig(payload) {
    const cacheKey = JSON.stringify(sort({
      fn: 'moduleGetFieldList',
      ...payload,
    }));
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey);
    }
    try {
      const response = await $http.post('/api/board/module/getConfig', payload);
      cache.set(cacheKey, response);
      return response;
    } catch (e) {
      return {};
    }
  },

  async createClipboard(compositionId, data = []) {
    try {
      const { clipboardId } = await $http.post(`/api/board/composition/${compositionId}/linkClipboard`, { data });
      return clipboardId;
    } catch (e) {
      console.error('Error creating clipboard', compositionId, data);
      return null;
    }
  },

  deleteItemFromCache(itemId) {
    cache.delete(itemId);
  },
};
